import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import useSetLayer from "@/core/helpers/useSetLayer";
import { Modal } from "@/shared/ui";
import { Layer } from "@/domains/chat/constants/layer";
import { setSettingsOpen } from "@/domains/chat/slice";
import SettingsBodyCommonContainer from "@/domains/chat/containers/settings/SettingsBodyCommonContainer";
import SettingsBodyMessageContainer from "@/domains/chat/containers/settings/SettingsBodyMessageContainer";
import SettingsButtons from "@/domains/chat/components/settings/SettingsButtons";
import SettingsResetButton from "@/domains/chat/components/settings/SettingsResetButton";
import SettingsTabs from "@/domains/chat/components/settings/SettingsTabs";
import useChatSettings from "@/domains/settings/chat/hooks/useChatSettings";
import {
  appliedChatSettingsSelector,
  defaultChatSettingsSelector,
  memoizedChatSettingsSelector,
  reset,
  setAppliedSettings,
  setMemoizedSettings,
} from "@/domains/settings/chat/slice";

enum Tab {
  Common = "Common",
  Message = "Message",
}

export default function SettingsContainer() {
  useSetLayer(Layer.CHAT_SETTINGS);
  const { t } = useTranslation("chat");
  const dispatch = useAppDispatch();
  const [currTab, setCurrTab] = useState<Tab>(Tab.Common);
  const [isPending, setIsPending] = useState(false);
  const appliedChatSettings = useAppSelector(appliedChatSettingsSelector);
  const defaultChatSettings = useAppSelector(defaultChatSettingsSelector);
  const memoizedChatSettings = useAppSelector(memoizedChatSettingsSelector);
  const { updateChatSettings } = useChatSettings();

  useEffect(() => {
    dispatch(setMemoizedSettings(appliedChatSettings));

    return () => {
      dispatch(reset());
    };
  }, [dispatch]);

  const closeHandler = useCallback(() => {
    dispatch(setAppliedSettings(memoizedChatSettings));
    dispatch(setSettingsOpen(false));
  }, [dispatch, memoizedChatSettings]);

  const resetToDefaultHandler = useCallback(async () => {
    setIsPending(true);
    await updateChatSettings(defaultChatSettings);
    setIsPending(false);
  }, [updateChatSettings, defaultChatSettings]);

  const saveHandler = useCallback(async () => {
    setIsPending(true);
    await updateChatSettings(appliedChatSettings);
    setIsPending(false);
  }, [appliedChatSettings, updateChatSettings]);

  const header = (
    <SettingsTabs
      tabs={[Tab.Common, Tab.Message]}
      currTab={currTab}
      onChange={(value) => setCurrTab(value as Tab)}
    />
  );

  const footer = (
    <>
      <SettingsResetButton onClick={resetToDefaultHandler} />
      <SettingsButtons isSaving={isPending} onCancel={closeHandler} onSave={saveHandler} />
    </>
  );

  if (!appliedChatSettings) {
    return null;
  }
  return (
    <Modal
      open
      onClose={closeHandler}
      title={t("chat.chatSettings")}
      header={header}
      footer={footer}
      className="w-[27rem]"
    >
      {currTab === Tab.Common && <SettingsBodyCommonContainer />}
      {currTab === Tab.Message && <SettingsBodyMessageContainer />}
    </Modal>
  );
}
